import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { StyleEnums } from "models/enums";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { FilterMoreActionsProps } from "models/propTypes/filterPropTypes";
import { IconDefinitionConstants } from "utils/constants";
import { FilterFormTypes } from "models/formTypes";
//#endregion
//#region components
import useSaveFilterDialog from "./forms/useSaveDialog";
import MenuButton from "../buttons/menuButton";
import Icon from "../icon";
import { Button } from "@mui/material";
import { useFilterService } from "serviceHooks";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";

const FilterMoreActions: React.FC<FilterMoreActionsProps> = (props) => {
  const filterService = useFilterService();
  const { selectedFilter, filterCategory, getFilterValues, onSelectedFilterUpdateSuccess, loadFilters } = props;
  const dispatch = useDispatch();

  const deleteFilter = async () => {
    if (selectedFilter === undefined) return;
    await filterService.deleteFilter(selectedFilter!.id);

    onSelectedFilterUpdateSuccess((savedFilters) => {
      const index = savedFilters.findIndex((f) => f.id === selectedFilter!.id);
      const updatedFilters = [...savedFilters];
      updatedFilters.splice(index, 1);
      return updatedFilters;
    });
  };

  const onRemoveFilterDefault = async () => {
    if (selectedFilter === undefined) return Promise.resolve();

    const filter = selectedFilter;
    if (filter) {
      return filterService
        .updateFilter(filter.id, filter.name!, filter.filterData, false, filter.returnRelativeDates)
        .then((r) => {
          onSelectedFilterUpdateSuccess((savedFilters) => {
            return [...savedFilters].map((f) => {
              if (f.id === filter.id) f.isDefault = false;
              return f;
            });
          });
        });
    }
  };

  const onSetFilterDefault = async () => {
    if (selectedFilter === undefined) return Promise.resolve();

    const filter = selectedFilter;
    if (filter) {
      return filterService
        .updateFilter(filter.id, filter.name!, filter.filterData, true, filter.returnRelativeDates)
        .then((r) => {
          onSelectedFilterUpdateSuccess((savedFilters) => {
            return [...savedFilters].map((f) => {
              if (f.id === filter.id) f.isDefault = true;
              else f.isDefault = false;
              return f;
            });
          });
        });
    }
  };

  const getSaveDialog = useSaveFilterDialog();

  const handleSubmit = async (values: FilterFormTypes.Save) => {
    const filterValues = getFilterValues();

    if (filterValues === undefined) {
      return Promise.resolve();
    }

    let filterId = values.id;
    const result = await filterService.saveFilter(
      values.name,
      filterCategory,
      filterValues,
      selectedFilter?.name === values.name ? selectedFilter?.isDefault ?? false : false,
      values.returnRelativeDates
    );

    filterId = result.data;
    await loadFilters(filterId);
  };

  const onSaveFilterAsClick = () => {
    if (!filterCategory) {
      throw Error("Should not be reachable");
    }

    dispatch(displayDialog(getSaveDialog(handleSubmit, selectedFilter)));

    return Promise.resolve();
  };

  const onSaveFilterClick = async () => {
    if (filterCategory === undefined || selectedFilter === undefined) {
      throw Error("Should not be reachable");
    }

    await handleSubmit({
      id: selectedFilter.id,
      name: selectedFilter.name ?? "",
      returnRelativeDates: selectedFilter.returnRelativeDates,
      isDefault: selectedFilter.isDefault,
    });
  };

  return (
    <MenuButton
      menuItems={[
        ...(selectedFilter?.id !== undefined
          ? !selectedFilter?.isDefault
            ? [
                {
                  description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Filter.SaveDefault),
                  icon: IconDefinitionConstants.WBell,
                  onClickHandler: onSetFilterDefault,
                  successMessage: TranslationKeys.Filter.SetDefaultSuccessMessage,
                },
              ]
            : [
                {
                  description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Filter.RemoveDefault),
                  icon: IconDefinitionConstants.WStop,
                  onClickHandler: onRemoveFilterDefault,
                  successMessage: TranslationKeys.Filter.SetDefaultSuccessMessage,
                },
              ]
          : []),
        {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Filter.SaveAs),
          icon: IconDefinitionConstants.WSave,
          onClickHandler: onSaveFilterAsClick,
        },
        ...(selectedFilter?.id !== undefined
          ? [
              {
                description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Filter.Save),
                icon: IconDefinitionConstants.WSave,
                onClickHandler: onSaveFilterClick,
                successMessage: TranslationKeys.Filter.SaveSuccessMessage,
              },
              ACTION_SEPARATOR,
              {
                description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Filter.Delete),
                icon: IconDefinitionConstants.WTrashAlt,
                onClickHandler: deleteFilter,
                hidden: selectedFilter?.id === undefined,
                highlightType: StyleEnums.DisplayType.Danger,
                successMessage: TranslationKeys.Filter.DeleteSuccessMessage,
              },
            ]
          : []),
      ]}
      menuPlacement="bottom-start"
      button={
        <Button className={cssExports.drawerMenuIcon}>
          <Icon icon={IconDefinitionConstants.WEllipsisH} />
        </Button>
      }
    />
  );
};

export default FilterMoreActions;
